import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamProjectSlug, ParamRegionUrl, ParamTeamSlug } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { structuredResult } from "./results-NF1RJbDZ.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/remove-team-from-project.ts
const assignedTeamSchema = z.object({
	id: z.string(),
	slug: z.string(),
	name: z.string()
});
const removeTeamFromProjectOutputSchema = z.object({
	changed: z.boolean(),
	teams: z.array(assignedTeamSchema)
});
function mapTeam(team) {
	return {
		id: String(team.id),
		slug: team.slug,
		name: team.name
	};
}
var remove_team_from_project_default = defineTool({
	name: "remove_team_from_project",
	skills: ["project-management"],
	requiredScopes: [
		"project:write",
		"team:read",
		"org:read"
	],
	description: [
		"Revoke a team's access to an existing Sentry project.",
		"",
		"Use this tool when you need to:",
		"- Remove a team from a project",
		"- Revoke team access without changing project metadata",
		"- Check project team assignments before removing access",
		"",
		"Be careful when using this tool because it revokes project access.",
		"",
		"<examples>",
		"remove_team_from_project(organizationSlug='my-organization', projectSlug='my-project', teamSlug='my-team')",
		"</examples>",
		"",
		"<hints>",
		"- The team must already be assigned to the project.",
		"- This tool will not remove the last team assigned to a project.",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		projectSlug: ParamProjectSlug,
		teamSlug: ParamTeamSlug
	},
	annotations: {
		readOnlyHint: false,
		destructiveHint: true,
		openWorldHint: true
	},
	outputSchema: removeTeamFromProjectOutputSchema,
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		setTag("project.slug", params.projectSlug);
		setTag("team.slug", params.teamSlug);
		const currentTeams = await apiService.listProjectTeams({
			organizationSlug,
			projectSlug: params.projectSlug
		});
		const isAssigned = currentTeams.some((team) => team.slug === params.teamSlug);
		if (!isAssigned) throw new UserInputError("The specified team is not assigned to this project. Choose one of the current project teams before removing access.");
		if (currentTeams.length <= 1) throw new UserInputError("Cannot remove the last team assigned to a project. Add another team to the project before removing this team.");
		await apiService.removeTeamFromProject({
			organizationSlug,
			projectSlug: params.projectSlug,
			teamSlug: params.teamSlug
		});
		const updatedTeams = await apiService.listProjectTeams({
			organizationSlug,
			projectSlug: params.projectSlug
		});
		return structuredResult({
			changed: true,
			teams: updatedTeams.map(mapTeam)
		});
	}
});

//#endregion
export { removeTeamFromProjectOutputSchema, remove_team_from_project_default };
//# sourceMappingURL=remove-team-from-project-CT7eqBf7.js.map